import axios from "axios";
import { saveReturnPath } from "./navigationState";
import { clearSessionToken, getSessionToken } from "./sessionToken";

const DEFAULT_PUBLIC_BACKEND = "https://taistu-production.up.railway.app";

/**
 * HTTP API origin. On Vercel this is same-origin (empty → `/api` via rewrite),
 * so the session cookie is first-party. Locally it points at the FastAPI port.
 */
export function resolveBackendOrigin() {
  const env = process.env.REACT_APP_BACKEND_URL;
  if (env != null && String(env).trim()) return String(env).replace(/\/$/, "");
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") return "http://localhost:8001";
    // Deployed: same-origin `/api` (see frontend/vercel.json rewrite).
    return "";
  }
  return DEFAULT_PUBLIC_BACKEND;
}

/**
 * WebSocket origin — hit Railway directly; Vercel rewrites may not upgrade WS.
 */
export function resolveWsBackendOrigin() {
  const env = process.env.REACT_APP_WS_URL || process.env.REACT_APP_BACKEND_URL;
  if (env != null && String(env).trim()) return String(env).replace(/\/$/, "");
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") return "http://localhost:8001";
  }
  return DEFAULT_PUBLIC_BACKEND;
}

const API_BASE = `${resolveBackendOrigin()}/api`;

export const api = axios.create({
  baseURL: API_BASE || "/api",
  withCredentials: true, // httpOnly session cookie
});

/** FastAPI often returns `detail` as a string, object, or array of validation errors — never pass it raw to React/toast. */
export function formatApiDetail(detail, fallback = "Request failed") {
  if (detail == null || detail === "") return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (item == null) return "";
      if (typeof item === "string") return item;
      if (typeof item === "object" && typeof item.msg === "string") return item.msg;
      try {
        return JSON.stringify(item);
      } catch {
        return "";
      }
    });
    const joined = parts.filter(Boolean).join("; ");
    return joined || fallback;
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}

function readAuthToken() {
  const mem = getSessionToken();
  if (mem) return mem;
  try {
    return localStorage.getItem("taistu_token") || null;
  } catch {
    return null;
  }
}

function clearClientAuthArtifacts() {
  clearSessionToken();
  try {
    localStorage.removeItem("taistu_token");
  } catch {
    // ignore
  }
}

/** Auth probes handle 401 themselves — do not hard-redirect mid login/bootstrap. */
function isAuthBootstrapUrl(url) {
  const u = String(url || "");
  return (
    u.includes("/auth/me") ||
    u.includes("/auth/login") ||
    u.includes("/auth/logout") ||
    u.includes("/auth/verify") ||
    u.includes("/auth/exchange") ||
    u.includes("/auth/2fa/") ||
    u.includes("/auth/request-magic-link") ||
    u.includes("/auth/session")
  );
}

function setAuthHeader(headers, token) {
  if (!headers || !token) return;
  const value = `Bearer ${token}`;
  if (typeof headers.set === "function") {
    headers.set("Authorization", value);
  } else {
    headers.Authorization = value;
  }
}

api.interceptors.request.use((config) => {
  // Cookie is primary on same-origin; Bearer covers WS / legacy / cookie gaps.
  const token = readAuthToken();
  if (token) setAuthHeader(config.headers, token);
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers?.set) {
      config.headers.set("Content-Type", undefined);
    } else if (config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  return config;
});

let handlingUnauthorized = false;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401) {
      const reqUrl = String(err.config?.url || "");
      const isPortalFileBlobDownload =
        reqUrl.includes("/portal/files/item/") && reqUrl.includes("/download");
      if (!isPortalFileBlobDownload && !isAuthBootstrapUrl(reqUrl)) {
        // Confirm the session is actually dead before nuking the SPA.
        // A single endpoint 401 (race, stale tab) must not flash-logout.
        if (!handlingUnauthorized && readAuthToken()) {
          handlingUnauthorized = true;
          try {
            await api.get("/auth/me");
            // Session still valid — do not redirect; surface the original error.
            handlingUnauthorized = false;
            return Promise.reject(err);
          } catch {
            handlingUnauthorized = false;
          }
        }
        clearClientAuthArtifacts();
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/verify") &&
          window.location.pathname !== "/"
        ) {
          saveReturnPath();
          window.location.href = "/";
        }
      }
    }
    return Promise.reject(err);
  }
);

export const API_BASE_URL = API_BASE || "/api";

export async function postMultipart(path, formData) {
  const headers = new Headers();
  const token = readAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const origin = resolveBackendOrigin();
  const res = await fetch(`${origin}/api${normalized}`, {
    method: "POST",
    headers,
    body: formData,
    credentials: "include",
  });
  if (!res.ok) {
    let detail = "Request failed";
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      try {
        const body = await res.json();
        if (typeof body?.detail === "string") detail = body.detail;
        else if (body?.detail) detail = JSON.stringify(body.detail);
      } catch {
        // ignore parse errors
      }
    } else {
      const text = await res.text();
      if (text) detail = text;
    }
    const err = new Error(detail);
    err.response = { status: res.status, data: { detail } };
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return res.json();
  return null;
}

export function toAbsoluteAssetUrl(src) {
  if (!src) return src;
  const s = String(src);
  // Backend stores public asset URLs like "/api/portal/files/.../download".
  // Same-origin `/api` proxy works with relative URLs; absolute for cross-origin/local.
  if (s.startsWith("/")) {
    const origin = resolveBackendOrigin();
    return origin ? `${origin}${s}` : s;
  }
  return s;
}
